import { useMemo } from 'react';
import { useListToolCatalog } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import type { MentionGroup, MentionSource } from '../types';

/**
 * Returns a MentionSource for agent tools.
 *
 * @param enabledToolTypes - Only include tools whose `type` appears in this list.
 *   Typically pass `definition.tools.map(t => t.toolType)` from the agent definition.
 * @param subAgentToolTypes - Tool types that run as delegated sub-agents (e.g. the
 *   A2A browser operator). Their chips render an agent icon instead of a tool icon.
 */
export function useToolMentionSource(
  enabledToolTypes: string[],
  subAgentToolTypes: string[] = [],
): MentionSource {
  const { data } = useListToolCatalog();
  // Stable reference — only changes when actual catalog data changes, not on every render
  const tools = data?.tools;

  // Stable set of enabled types for efficient lookup
  const enabledSet = useMemo(
    () => new Set(enabledToolTypes),
    [enabledToolTypes],
  );
  const subAgentSet = useMemo(
    () => new Set(subAgentToolTypes),
    [subAgentToolTypes],
  );

  return useMemo<MentionSource>(() => {
    // Filter to only the tools this agent has enabled
    const filteredTools = (tools ?? []).filter((t) => enabledSet.has(t.type));

    return {
      key: 'tool',
      label: 'Tools',

      getGroups(query: string): MentionGroup[] {
        const q = query.toLowerCase();

        // Group by category
        const categoryMap = new Map<string, typeof filteredTools>();
        for (const tool of filteredTools) {
          const category = tool.category ?? 'Other';
          const arr = categoryMap.get(category) ?? [];
          arr.push(tool);
          categoryMap.set(category, arr);
        }

        const groups: MentionGroup[] = [];
        for (const [category, categoryTools] of categoryMap) {
          const matched = categoryTools.filter(
            (t) =>
              !q ||
              t.name.toLowerCase().includes(q) ||
              t.description?.toLowerCase().includes(q),
          );
          if (matched.length > 0) {
            groups.push({
              label: category,
              options: matched.map((t) => ({
                id: t.type,
                displayName: t.name,
                description: t.description,
              })),
            });
          }
        }

        return groups;
      },

      resolveDisplayName(id: string): string | undefined {
        const tool = filteredTools.find((t) => t.type === id);
        return tool?.name;
      },

      resolveKind(id: string) {
        return subAgentSet.has(id) ? 'agent' : 'tool';
      },
    };
  }, [tools, enabledSet, subAgentSet]);
}
